import EmptyState from '@/components/ui/EmptyState'
import { useAuth } from '@/hooks/useAuth'
import { useAuthModal } from '@/contexts/AuthModalContext'
import { useUnreadMessages } from '@/contexts/MessagesContext'
import type { InboxConversation } from '@/lib/messages'
import { invokeMessages, messageThreadPath, messagesError } from '@/lib/messages'
import { getStorePath } from '@/lib/storeSlug'
import { formatRelativeTime } from '@/lib/time'
import {Mail} from 'lucide-react'
import { useEffect, useState } from 'react'
import toast from 'react-hot-toast'
import { Link } from 'react-router-dom'

function Messages() {
  const { isAuthenticated } = useAuth()
  const { openAuth } = useAuthModal()
  const { refreshUnread } = useUnreadMessages()
  const [conversations, setConversations] = useState<InboxConversation[]>([])
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    if (!isAuthenticated) {
      setConversations([])
      setLoading(false)
      return
    }

    let active = true
    const loadInbox = async (silent = false) => {
      if (!silent) setLoading(true)
      try {
        const result = await invokeMessages<{ conversations?: InboxConversation[] }>({ action: 'inbox' })
        if (!active) return
        setConversations(result?.conversations || [])
        await refreshUnread()
      } catch (error) {
        if (active && !silent) toast.error(messagesError(error))
      } finally {
        if (active && !silent) setLoading(false)
      }
    }

    void loadInbox()
    const timer = window.setInterval(() => {
      void loadInbox(true)
    }, 15000)
    return () => {
      active = false
      window.clearInterval(timer)
    }
  }, [isAuthenticated, refreshUnread])

  if (!isAuthenticated) {
    return (
      <div className="mx-auto max-w-lg px-4 py-16">
        <EmptyState
          icon={Mail}
          title="Sign in to message shops"
          description="Log in to talk with collectors about singles, sealed product, and trades."
          action={
            <button
              type="button"
              onClick={openAuth}
              className="rounded-full bg-[#FFDE00] px-5 py-2.5 text-sm font-extrabold text-black shadow-[0_4px_0_#c9a800]"
            >
              Sign in
            </button>
          }
        />
      </div>
    )
  }

  return (
    <div className="mx-auto max-w-2xl px-4 py-8 sm:px-6">
      <div className="mb-6">
        <p className="inline-flex items-center gap-2 rounded-full border-2 border-[#FFDE00]/70 bg-[#FFDE00] px-3 py-1 text-[11px] font-extrabold uppercase tracking-wide text-black">
          <Mail className="h-3.5 w-3.5" />
          Inbox
        </p>
        <h1 className="font-display mt-3 text-3xl font-extrabold text-white">Messages</h1>
        <p className="mt-1 text-sm font-semibold text-white/60">
          Private chats with shops. Opening a thread marks it as read.
        </p>
      </div>

      {loading ? (
        <div className="space-y-3">
          {Array.from({ length: 4 }).map((_, index) => (
            <div key={index} className="h-20 animate-pulse rounded-3xl bg-white/5" />
          ))}
        </div>
      ) : conversations.length === 0 ? (
        <EmptyState
          icon={Mail}
          title="No messages yet"
          description="Visit a store and tap Message to start a conversation."
          action={
            <Link to="/discover" className="rounded-full bg-[#FFDE00] px-5 py-2.5 text-sm font-extrabold text-black shadow-[0_4px_0_#c9a800]">
              Discover stores
            </Link>
          }
        />
      ) : (
        <div className="space-y-2">
          {conversations.map(conversation => {
            const unread = Number(conversation.unreadCount || 0)
            return (
              <Link
                key={conversation._id || conversation.peerId}
                to={messageThreadPath(conversation.peerId)}
                className="flex items-center gap-3 rounded-3xl border-2 border-white/10 bg-white/5 px-3 py-3 transition-colors hover:border-[#FFDE00]/60 hover:bg-white/10"
              >
                <div className="h-12 w-12 shrink-0 overflow-hidden rounded-full border-2 border-[#FFDE00]/70 bg-[#3B4CCA]">
                  {conversation.avatarUrl ? (
                    <img src={conversation.avatarUrl} alt="" className="h-full w-full object-cover" />
                  ) : (
                    <div className="flex h-full w-full items-center justify-center text-lg font-extrabold text-white">
                      {(conversation.storeName || '?')[0]?.toUpperCase()}
                    </div>
                  )}
                </div>
                <div className="min-w-0 flex-1">
                  <div className="flex items-center justify-between gap-2">
                    <p className="truncate font-display text-base font-extrabold text-white">
                      {conversation.storeName || 'Collector'}
                    </p>
                    <span className="shrink-0 text-[11px] font-bold text-white/40">
                      {formatRelativeTime(conversation.lastMessageAt)}
                    </span>
                  </div>
                  <p className={`truncate text-sm font-semibold ${unread > 0 ? 'text-white' : 'text-white/50'}`}>
                    {conversation.lastMessage || 'No messages yet'}
                  </p>
                </div>
                {unread > 0 ? (
                  <span className="min-w-6 rounded-full bg-[#FF1A1A] px-2 py-0.5 text-center text-[11px] font-extrabold text-white">
                    {unread > 99 ? '99+' : unread}
                  </span>
                ) : null}
              </Link>
            )
          })}
        </div>
      )}

      {conversations.length > 0 ? (
        <p className="mt-6 text-center text-xs font-semibold text-white/35">
          Store links stay public. Chats stay private.
          {conversations[0]?.storeSlug ? (
            <>
              {' '}
              Visit{' '}
              <Link to={getStorePath(conversations[0])} className="text-[#FFDE00] underline-offset-2 hover:underline">
                a shop
              </Link>{' '}
              anytime.
            </>
          ) : null}
        </p>
      ) : null}
    </div>
  )
}

export default Messages
